import { z } from 'zod';
import { WorkflowDefinitionSchema } from '@/types/workflowDefinition';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Template provenance — built-in (hand-crafted) vs community (synced from upstream) */
export const TEMPLATE_SOURCES = ['built-in', 'community'] as const;
export type TemplateSource = (typeof TEMPLATE_SOURCES)[number];

/** All supported template gallery categories */
export const TEMPLATE_CATEGORIES = [
  'ai',
  'sales',
  'it-ops',
  'marketing',
  'engineering',
  'support',
  'operations',
  'communication',
  'api',
  'summarization',
  'categorization',
  'finance',
  'document-processing',
  'data',
  'other',
] as const;

export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

export const MAX_TEMPLATE_CATEGORIES = 3;
export const OTHER_TEMPLATE_CATEGORY = 'other';
export const DEFAULT_AUTHORED_TEMPLATE_CATEGORIES = ['ai'] as const;

export function isTemplateCategory(value: unknown): value is TemplateCategory {
  return typeof value === 'string' && TEMPLATE_CATEGORIES.some((category) => category === value);
}

// =============================================================================
// SCHEMAS
// =============================================================================

/** Authoring rules are stricter than the backward-compatible import/storage path. */
export const authoredTemplateCategoriesSchema = z
  .array(z.enum(TEMPLATE_CATEGORIES))
  .min(1, { message: 'Select at least one category.' })
  .max(MAX_TEMPLATE_CATEGORIES, { message: 'Select up to 3 categories.' })
  .refine((categories) => new Set(categories).size === categories.length, {
    message: 'Select each category only once.',
  })
  .refine(
    (categories) => !categories.includes(OTHER_TEMPLATE_CATEGORY) || categories.length === 1,
    { message: 'Other must be selected on its own.' },
  );

export function validateAuthoredTemplateCategories(
  value: unknown,
): { success: true; data: TemplateCategory[] } | { success: false; errors: string[] } {
  const result = authoredTemplateCategoriesSchema.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return {
    success: false,
    errors: result.error.issues.map((issue) => `metadata.category: ${issue.message}`),
  };
}

/**
 * Describes a single connection that a template requires.
 * Templates declare *requirements* (connector type + API ID), not live
 * connection references — the user satisfies requirements at deploy time.
 * Display names and other metadata are fetched at runtime based on apiId.
 */
export const templateConnectionRequirementSchema = z.object({
  /**
   * Connection type: 'shared' for managed APIs, 'inapp' for built-in connectors, 'agent' for agent connections.
   */
  connectorType: z.enum(['shared', 'inapp', 'agent']),

  /**
   * API identifier path, e.g. '/managedApis/sql' or '/serviceProviders/AzureBlob'.
   * Used to look up connector metadata (display name, icon, etc.) at runtime.
   */
  apiId: z.string().min(1),

  /**
   * Connection reference key used in the workflow definition.
   * This is the key in the connections object (e.g., 'sql_myWorkflow').
   * When provided, placeholder connections use this exact key.
   */
  referenceKey: z.string().min(1).optional(),
});

export type TemplateConnectionRequirement = z.infer<typeof templateConnectionRequirementSchema>;

/**
 * Metadata used when rendering a template card in the gallery.
 * Does not include the full workflow definition — kept small for list views.
 */
export const templateManifestSchema = z.object({
  /** Stable, URL-safe identifier for this template (e.g. "approval-email-teams"). */
  id: z.string().min(1),

  /** Human-readable template name displayed in the gallery. */
  name: z.string().min(1),

  /** One-to-two sentence description of what the template does. */
  description: z.string().min(1),

  /**
   * Gallery categories used for filtering. Imported/storage templates retain
   * all recognized categories, including more than three or Other alongside
   * named categories. Only newly authored templates use the stricter rules.
   */
  category: z.array(z.enum(TEMPLATE_CATEGORIES)).min(1),

  /**
   * Free-form search / filter tags (e.g. ["email", "approval", "teams"]).
   * All values are lower-cased at validation time.
   */
  tags: z.array(z.string().min(1).transform((tag) => tag.toLowerCase())).default([]),

  /** Connections this template requires. */
  connectionRequirements: z.array(templateConnectionRequirementSchema).default([]).refine(
    (reqs) => new Set(reqs.map(r => r.apiId)).size === reqs.length,
    { message: 'connectionRequirements must have unique apiId values' }
  ),

  /**
   * Optional ordering hint for the "Featured" row in the gallery.
   * Lower numbers appear first. Omit to exclude from the featured row.
   */
  featuredOrder: z.number().int().nonnegative().optional(),

  /** Provenance: built-in (hand-crafted) or community (synced from upstream repo). */
  source: z.enum(TEMPLATE_SOURCES).default('built-in'),

  /** Author display name, shown on community gallery cards ("By <author>"). */
  author: z.string().min(1).optional(),

  /**
   * Connector ids to display as icons on gallery cards, e.g.
   * '/managedApis/office365' or 'connectionProviders/request'. Display names
   * and icons are resolved at runtime from the id.
   */
  featuredConnectors: z.array(z.string().min(1)).optional(),
});

export type TemplateManifest = z.infer<typeof templateManifestSchema>;

/**
 * Zod schema for connections.json structure embedded inside a template.
 * Mirrors the `ConnectionsConfiguration` type from logicApps.ts as a loose
 * schema — individual connection shapes are validated at apply time, not here.
 */
const templateConnectionsConfigSchema = z
  .object({
    managedApiConnections: z.record(z.string(), z.unknown()).optional(),
    serviceProviderConnections: z.record(z.string(), z.unknown()).optional(),
    agentConnections: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();

/**
 * Zod schema for a single parameter definition embedded in a template.
 * Matches the `ParameterDefinition` shape from logicApps.ts.
 */
const templateParameterDefinitionSchema = z.object({
  type: z.string().min(1),
  value: z.unknown().optional(),
  metadata: z
    .object({
      description: z.string().optional(),
    })
    .optional(),
  // Extended fields for richer template parameter UI
  displayName: z.string().optional(),
  description: z.string().optional(),
  required: z.boolean().optional(),
  defaultValue: z.unknown().optional(),
  allowedValues: z.array(z.unknown()).optional(),
});

/**
 * Full template definition — manifest metadata plus the runtime artifacts
 * (workflow definition, connections skeleton, optional parameters).
 *
 * This is the shape of the static JSON files loaded from the template library.
 */
export const templateDefinitionSchema = templateManifestSchema.extend({
  /**
   * The complete workflow definition (workflow.json shape).
   * Validated against `WorkflowDefinitionSchema`.
   */
  workflowDefinition: WorkflowDefinitionSchema,

  /**
   * Skeleton connections.json for the template.
   * Connection IDs are placeholder values replaced when the user deploys.
   */
  connectionsConfiguration: templateConnectionsConfigSchema.optional(),

  /**
   * Optional parameters.json content bundled with the template.
   * Keys are parameter names; values follow `ParameterDefinition` shape.
   */
  parameters: z.record(z.string(), templateParameterDefinitionSchema).optional(),
});

export type TemplateDefinition = z.infer<typeof templateDefinitionSchema>;

// =============================================================================
// VALIDATION HELPER
// =============================================================================

/**
 * Validate an unknown value against `templateDefinitionSchema`.
 *
 * Returns a discriminated result:
 * - `{ success: true,  data: TemplateDefinition }` on success
 * - `{ success: false, errors: string[] }`          on failure
 *
 * @example
 * const result = validateTemplate(JSON.parse(rawJson));
 * if (result.success) {
 *   console.log(result.data.name);
 * } else {
 *   console.error(result.errors);
 * }
 */
export function validateTemplate(data: unknown): { success: true; data: TemplateDefinition } | { success: false; errors: string[] } {
  const result = templateDefinitionSchema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  const errors = result.error.issues.map((issue) => {
    const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
    return `${path}${issue.message}`;
  });

  return { success: false, errors };
}

// =============================================================================
// Display helpers — shared across TemplateCard, TemplatePreview, etc.
// =============================================================================

export const CATEGORY_DISPLAY: Record<TemplateCategory, string> = {
  ai: 'AI',
  sales: 'Sales',
  'it-ops': 'IT Ops',
  marketing: 'Marketing',
  engineering: 'Engineering',
  support: 'Support',
  operations: 'Operations',
  communication: 'Communication',
  api: 'API',
  summarization: 'Summarization',
  categorization: 'Categorization',
  finance: 'Finance',
  'document-processing': 'Document Processing',
  data: 'Data',
  other: 'Other',
};
